type RoutineGroup = { category: string; operation: string; collection: string };
type Summary = RoutineGroup & { message: string; repetitions: number; firstAt: number; lastAt: number; lastMessage: string; itemIds: string[] };
type Entry = Summary & { emit: (summary: Summary) => void };

const classifyRoutineLog = (category: string, message: string): RoutineGroup | null => {
  // Apenas sucessos técnicos conhecidos; warnings, mídia e marcos de negócio não passam aqui.
  if (message.length > 1000) return null;
  if (!/^(SQLITE-ADAPTER|DEXIE-ADAPTER|DATABASE-SERVICE):(LIST_ITEMS|READ_ITEM|CREATE_ITEM|UPDATE_ITEM)$/.test(category)) return null;
  const list = /^Coleção: ([a-z_]+) \|/.exec(message);
  if (category.endsWith(':LIST_ITEMS') && list) return { category, operation: 'list', collection: list[1] };
  const pending = /^Pulando registro de operação pendente para item ([a-z_]+)\//.exec(message);
  if (pending) return { category, operation: 'skip_pending', collection: pending[1] };
  const item = /^(Criando item|Item criado com sucesso|Item atualizado com sucesso|Item encontrado|Item não encontrado) na coleção "([a-z_]+)"/.exec(message);
  if (item) return { category, operation: item[1], collection: item[2] };
  return null;
};

export class RoutineLogSummary {
  private entries = new Map<string, Entry>();
  private timer: ReturnType<typeof setTimeout> | undefined;
  constructor(private readonly now = Date.now, private readonly windowMs = 5000) {}

  suppress(category: string, message: string, emit: Entry['emit']): boolean {
    // Ausências são evidência individual; o flush mantém a ordem encontrado → ausente → encontrado.
    if (category.endsWith(':READ_ITEM') && message.startsWith('Item não encontrado')) {
      const collection = /na coleção "([a-z_]+)"/.exec(message)?.[1];
      for (const [key, entry] of this.entries) {
        if (entry.category === category && entry.collection === collection) {
          this.entries.delete(key);
          this.emitEntry(entry);
        }
      }
      return false;
    }
    const group = classifyRoutineLog(category, message);
    if (!group) return false;
    // Mudanças de listagem e IDs operacionais continuam visíveis; catálogos conservam todos os IDs em lotes limitados.
    const isCatalog = /^(guincho_campos_template|guincho_dropdown|guincho_templates_checklist)$/.test(group.collection);
    const itemId = /com ID "([^"]+)"/.exec(message)?.[1]
      ?? /Pulando registro de operação pendente para item [a-z_]+\/(\S+)/.exec(message)?.[1];
    // IDs longos ficam individuais para não serem truncados na serialização do Sentry.
    if (itemId && JSON.stringify([itemId]).length > 900) return false;
    const key = group.operation === 'list' || (isCatalog && itemId)
      ? `${group.category}:${group.operation}:${group.collection}`
      : `${group.category}:${message}`;
    let previous = this.entries.get(key);
    if (previous && group.operation === 'list' && previous.lastMessage !== message) {
      this.entries.delete(key);
      this.emitEntry(previous);
      previous = undefined;
    }
    if (previous && itemId && !previous.itemIds.includes(itemId) && (previous.itemIds.length >= 16 || JSON.stringify([...previous.itemIds, itemId]).length > 900)) {
      this.entries.delete(key);
      this.emitEntry(previous);
      previous = undefined;
    }
    if (previous) {
      if (itemId && !previous.itemIds.includes(itemId)) previous.itemIds.push(itemId);
      previous.repetitions += 1;
      previous.lastAt = this.now();
      previous.lastMessage = message;
      return true;
    }
    // Limite independente da cardinalidade dos dados recebidos.
    if (this.entries.size >= 64) this.flush();
    const at = this.now();
    this.entries.set(key, { ...group, message, repetitions: 1, firstAt: at, lastAt: at, lastMessage: message, itemIds: itemId ? [itemId] : [], emit });
    this.timer ??= setTimeout(() => this.flush(), this.windowMs);
    (this.timer as ReturnType<typeof setTimeout> & { unref?: () => void }).unref?.();
    return false;
  }

  flush(): void {
    const entries = [...this.entries.values()];
    this.clear();
    for (const entry of entries) this.emitEntry(entry);
  }

  private emitEntry({ emit, ...summary }: Entry): void {
    if (summary.repetitions <= 1) return;
    try { emit(summary); } catch { /* Telemetria não pode interromper o fluxo. */ }
  }

  clear(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = undefined;
    this.entries.clear();
  }
}

export const routineLogSummary = new RoutineLogSummary();
